import {
  PdfGen,
  PdfUnit,
  PdfSize,
  PdfRect,
  PdfPoint,
  PdfFont,
  PdfBrush,
  PdfPen,
  PdfColor,
  ETextAlign,
  PageGen,
} from '../../pdf/pdf';
import { ILayoutLabelTextGen } from '../layoutLabelText';
import { OptsLabelText } from '../optsLabelText';
import { GUtil } from '../../pdf/gutil';
import { IItemCount } from '@educraft/base';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';
import '../../pdf/draw/label';

/**
 * Shared base class for handwriting worksheet variants
 */
abstract class PisankaBase implements ILayoutLabelTextGen, IItemCount {
  abstract countDefault(): number;
  abstract countMin(): number;
  abstract countMax(): number;

  protected linesPerItem = 1;
  protected itemsPerPageLimit = 0;

  public async generate(pdf: PdfGen, opts: OptsLabelText): Promise<void> {
    const margin = PdfUnit.FromMillimeters(20);
    const lineWidth = PdfUnit.FromMillimeters(12);
    const lineSetHeight = lineWidth * 1.5;

    // Determine how many items fit on one page
    const availableHeight = PdfSize.A4.height - 2 * margin;
    const itemHeight =
      this.linesPerItem * lineSetHeight +
      (this.linesPerItem > 1 ? PdfUnit.FromMillimeters(10) : PdfUnit.FromMillimeters(5));
    let itemsPerPage = Math.max(1, Math.floor(availableHeight / itemHeight));

    if (this.itemsPerPageLimit > 0) {
      itemsPerPage = Math.min(itemsPerPage, this.itemsPerPageLimit);
    }

    const count = Math.min(opts.list.length, this.countMax());
    const list = opts.list.slice(0, count);

    for (let i = 0; i < list.length; i += itemsPerPage) {
      const page = pdf.addPage();
      const pageItems = list.slice(i, i + itemsPerPage);

      const cellHeight = availableHeight / itemsPerPage;
      const imgSize = Math.min(
        cellHeight - PdfUnit.FromMillimeters(10),
        PdfUnit.FromMillimeters(40)
      );

      const topPen = new PdfPen(PdfColor.fromCmyk(1, 0.5, 0, 0), GUtil.basePen.thickness);
      const middlePen = new PdfPen(PdfColor.fromCmyk(1, 0.5, 0, 0), GUtil.basePen.thickness);
      middlePen.dashArray = [1, 2];
      const {basePen} = GUtil;

      // Adjust font for images
      const allLabelTexts = pageItems.map((item) => [item.label.getText() ?? '']);
      const labelFont = page.adjustFontMulti(
        allLabelTexts,
        GUtil.baseFont,
        new PdfSize(imgSize, imgSize)
      );

      for (let j = 0; j < pageItems.length; j++) {
        const itemTop = margin + j * cellHeight;
        const imgRect = new PdfRect(margin, itemTop + (cellHeight - imgSize) / 2, imgSize, imgSize);

        // Draw image
        page.drawLabel(pageItems[j].label, { rect: imgRect, font: labelFont });

        const lineLeft = margin + imgSize + PdfUnit.FromMillimeters(10);
        const lineRight = PdfSize.A4.width - margin;

        // Draw lines centered vertically in the cell
        const totalLinesHeight = this.linesPerItem * lineSetHeight;
        const firstLineTop = itemTop + (cellHeight - totalLinesHeight) / 2;

        for (let l = 0; l < this.linesPerItem; l++) {
          const lineTop = firstLineTop + l * lineSetHeight;

          // Draw guidelines
          page.drawLine(topPen, new PdfPoint(lineLeft, lineTop), new PdfPoint(lineRight, lineTop));
          page.drawLine(
            middlePen,
            new PdfPoint(lineLeft, lineTop + lineWidth * 0.5),
            new PdfPoint(lineRight, lineTop + lineWidth * 0.5)
          );
          page.drawLine(
            basePen,
            new PdfPoint(lineLeft, lineTop + lineWidth * 1.0),
            new PdfPoint(lineRight, lineTop + lineWidth * 1.0)
          );
          page.drawLine(
            middlePen,
            new PdfPoint(lineLeft, lineTop + lineWidth * 1.5),
            new PdfPoint(lineRight, lineTop + lineWidth * 1.5)
          );

          const textRect = new PdfRect(lineLeft, lineTop, lineRight - lineLeft, lineWidth);
          this.drawLineContent(page, pageItems[j].text, l, textRect);
        }
      }
    }
  }

  protected abstract drawLineContent(
    page: PageGen,
    text: string,
    lineIndex: number,
    rect: PdfRect
  ): void;

  protected drawText(page: PageGen, text: string, brush: PdfBrush, rect: PdfRect) {
    page.drawString(text, GUtil.baseFont, brush, null, rect, ETextAlign.LeftBaseline);
  }

  protected get traceBrush(): PdfBrush {
    return new PdfBrush(PdfColor.fromCmyk(0, 0, 0, 0.3));
  }
}

/**
 * Basic Pisanka with guide text drawn once in black
 */
export class Pisanka__Basic extends PisankaBase {
  countDefault() {
    return 10;
  }
  countMin() {
    return 4;
  }
  countMax() {
    return 24;
  }
  protected linesPerItem = 1;
  protected itemsPerPageLimit = 10;

  protected drawLineContent(page: PageGen, text: string, lineIndex: number, rect: PdfRect) {
    this.drawText(page, text, PdfBrush.Black, rect);
  }
}

/**
 * Pisanka with blank guidelines
 */
export class Pisanka__Empty extends PisankaBase {
  countDefault() {
    return 10;
  }
  countMin() {
    return 4;
  }
  countMax() {
    return 24;
  }
  protected linesPerItem = 1;
  protected itemsPerPageLimit = 10;

  protected drawLineContent(page: PageGen, text: string, lineIndex: number, rect: PdfRect) {
    // Empty guidelines
  }
}

/**
 * Pisanka with guide text repeated in light gray for tracing
 */
export class Pisanka__Trace extends PisankaBase {
  countDefault() {
    return 10;
  }
  countMin() {
    return 4;
  }
  countMax() {
    return 24;
  }
  protected linesPerItem = 1;
  protected itemsPerPageLimit = 10;

  protected drawLineContent(page: PageGen, text: string, lineIndex: number, rect: PdfRect) {
    const repeated = [text, text, text].join('    ');
    this.drawText(page, repeated, this.traceBrush, rect);
  }
}

/**
 * Progressive training variant:
 * Line 1: solid black, Line 2: tracing, Line 3: first letter, Line 4: blank
 */
export class Pisanka__Progressive extends PisankaBase {
  countDefault() {
    return 6;
  }
  countMin() {
    return 2;
  }
  countMax() {
    return 12;
  }
  protected linesPerItem = 4;

  protected drawLineContent(page: PageGen, text: string, lineIndex: number, rect: PdfRect) {
    if (lineIndex === 0) {
      this.drawText(page, text, PdfBrush.Black, rect);
    } else if (lineIndex === 1) {
      const repeated = [text, text].join('    ');
      this.drawText(page, repeated, this.traceBrush, rect);
    } else if (lineIndex === 2) {
      this.drawText(page, text.charAt(0), PdfBrush.Black, rect);
    }
  }
}

/**
 * Multi-line variant: 4 sets of lines per word, only the first one has guide text
 */
export class Pisanka__MultiLine extends PisankaBase {
  countDefault() {
    return 6;
  }
  countMin() {
    return 2;
  }
  countMax() {
    return 12;
  }
  protected linesPerItem = 4;

  protected drawLineContent(page: PageGen, text: string, lineIndex: number, rect: PdfRect) {
    if (lineIndex === 0) {
      this.drawText(page, text, PdfBrush.Black, rect);
    }
  }
}

/**
 * Build-up variant: practices writing parts of the word progressively
 */
export class Pisanka__Syllables extends PisankaBase {
  countDefault() {
    return 6;
  }
  countMin() {
    return 2;
  }
  countMax() {
    return 12;
  }
  protected linesPerItem = 4;

  protected drawLineContent(page: PageGen, text: string, lineIndex: number, rect: PdfRect) {
    const parts = this.getSyllables(text);

    if (lineIndex === 0) {
      this.drawText(page, parts[0], PdfBrush.Black, rect);
    } else if (lineIndex === 1) {
      const twoParts = parts.length > 1 ? parts[0] + parts[1] : parts[0];
      this.drawText(page, twoParts, PdfBrush.Black, rect);
    } else if (lineIndex === 2) {
      this.drawText(page, text, this.traceBrush, rect);
    }
  }

  private getSyllables(text: string): string[] {
    const vowels = /[aeiouyáéíóúůýě]/i;
    const parts: string[] = [];
    let current = '';

    for (let i = 0; i < text.length; i++) {
      current += text[i];
      if (vowels.test(text[i])) {
        let hasMoreVowels = false;
        for (let j = i + 1; j < text.length; j++) {
          if (vowels.test(text[j])) {
            hasMoreVowels = true;
            break;
          }
        }

        if (hasMoreVowels) {
          let consonants = 0;
          while (i + consonants + 1 < text.length && !vowels.test(text[i + consonants + 1])) {
            consonants++;
          }
          if (consonants > 1) {
            current += text.substring(i + 1, i + 2);
            i++;
          }
          parts.push(current);
          current = '';
        }
      }
    }
    if (current) parts.push(current);
    if (parts.length === 0) return [text];
    return parts;
  }
}
